<template>
  <div class="sticky-menu" :style="menuStyles">
    <slot />
  </div>
</template>

<script>
import stickyMenuMixin, { SCROLL_DIRECTIONS } from './sticky-menu-mixin'

const CSS_POSITIONS = {
  absolute: 'absolute',
  fixed: 'fixed',
}

const DEFAULT_POSITIONS = {
  cssPosition: CSS_POSITIONS.absolute,
  cssTop: null,
  cssBottom: null,
}

export default {
  name: 'PositionFixedOrAbsolute',
  mixins: [stickyMenuMixin],

  data() {
    return {
      ...DEFAULT_POSITIONS,
      frozenTop: 0,
      hasReachedEdge: false,
      supportsPositionSticky: false,
    }
  },

  computed: {
    menuStyles() {
      const getValue = value => (value !== null ? `${value}px` : 'auto')
      return {
        position: this.cssPosition,
        top: getValue(this.cssTop),
        bottom: getValue(this.cssBottom),
      }
    },
  },

  methods: {
    onStickingToBottomChange(isSticking) {
      if (isSticking) this.setStickingToBottomPosition()
      else if (this.isMounted) this.setAbsolutePosition(SCROLL_DIRECTIONS.up)
    },

    resetInitialPosition() {
      this.cssPosition = DEFAULT_POSITIONS.cssPosition
      this.cssTop = DEFAULT_POSITIONS.cssTop
      this.cssBottom = DEFAULT_POSITIONS.cssBottom
      this.lastScrollY = 0
    },

    isItScrollingDown(forcedDirection) {
      return forcedDirection
        ? forcedDirection === SCROLL_DIRECTIONS.down
        : this.lastScrollY < this.getCurrentScrollY()
    },

    afterHeightChange() {
      if (!this.isActive) return

      if (!this.isStickingToBottom) {
        this.setAbsolutePosition(
          this.isScrollingDown ? SCROLL_DIRECTIONS.down : SCROLL_DIRECTIONS.up
        )
      }
      this.enableScroll()
    },

    updateHasReachedEdge(forcedDirection) {
      const currentScrollY = this.getCurrentScrollY()
      const winHeight = window.innerHeight
      const isScrollingDown = this.isItScrollingDown(forcedDirection)

      const pos = { top: this.frozenTop + this.parentLayout.top }
      pos.bottom = pos.top + this.$el.offsetHeight

      const spaceLeftBeforeEdge = isScrollingDown
        ? pos.bottom - winHeight - currentScrollY
        : currentScrollY - pos.top + this.parentLayout.top

      this.hasReachedEdge = spaceLeftBeforeEdge <= 0
    },

    getComputedFrozenTop() {
      return this.$el.getBoundingClientRect().top + this.getCurrentScrollY() - this.headerHeight
    },

    setStickingToBottomPosition() {
      this.cssPosition = CSS_POSITIONS.absolute
      this.cssTop = DEFAULT_POSITIONS.cssTop
      this.cssBottom = 0
      this.frozenTop = this.parentLayout.bottom - this.$el.offsetHeight - this.parentLayout.top
    },

    setWindowTallerThanMenuPosition() {
      this.setCssPosition()
    },

    setScrollingDownPosition(forcesReset) {
      this.setCssPosition(forcesReset ? SCROLL_DIRECTIONS.down : null)
      this.isScrollingDown = true
    },

    setScrollingUpPosition(forcesReset) {
      this.setCssPosition(forcesReset ? SCROLL_DIRECTIONS.up : null)
      this.isScrollingDown = false
    },

    setCssPosition(forcedDirection) {
      if (this.isStickingToBottom) return

      this.hasReachedEdge || this.hasWindowTallerThanMenu
        ? this.setFixedPosition(forcedDirection)
        : this.setAbsolutePosition(forcedDirection)
    },

    setFixedPosition(forcedDirection) {
      const currentScrollY = this.getCurrentScrollY()
      const winHeight = window.innerHeight
      const winHeightDiff = this.$el.offsetHeight - winHeight
      const isScrollingDown = this.isItScrollingDown(forcedDirection)

      this.cssPosition = CSS_POSITIONS.fixed
      this.cssTop = DEFAULT_POSITIONS.cssTop
      this.cssBottom = DEFAULT_POSITIONS.cssBottom
      let top = this.headerHeight

      if (this.hasWindowTallerThanMenu) {
        this.cssTop = this.headerHeight
      } else if (isScrollingDown) {
        this.cssTop = top = -winHeightDiff
      } else {
        this.cssBottom = -winHeightDiff - this.headerHeight
      }
      this.frozenTop = top + currentScrollY - this.headerHeight
    },

    setAbsolutePosition(forcedDirection) {
      const isScrollingDown = this.isItScrollingDown(forcedDirection)
      this.cssPosition = CSS_POSITIONS.absolute
      if (forcedDirection) this.frozenTop = this.getComputedFrozenTop()

      if (isScrollingDown) {
        this.cssTop = this.frozenTop
        this.cssBottom = DEFAULT_POSITIONS.cssBottom
      } else {
        this.cssTop = DEFAULT_POSITIONS.cssTop
        this.cssBottom = this.parentMaxHeight - this.frozenTop - this.$el.offsetHeight
      }
    },
  },
}
</script>
